"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";

interface UnsavedReportsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
}

export function UnsavedReportsDialog({
  open,
  onOpenChange,
  count,
}: UnsavedReportsDialogProps) {
  const router = useRouter();
  const [isProcessing, setIsProcessing] = useState(false);
  const title = useMemo(() => {
    if (count <= 0) return "No unsaved reports";
    return count === 1
      ? "You have 1 unsaved report"
      : `You have ${count} unsaved reports`;
  }, [count]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm p-6">
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg">{title}</DialogTitle>
          <DialogDescription className="text-sm">
            You created reports while not signed in. You can add them to your
            history now so you don’t lose them.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-3 flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              if (isProcessing) return;
              setIsProcessing(true);
              try {
                if (typeof window !== "undefined" && window.localStorage) {
                  window.localStorage.removeItem("unsavedReports");
                }
              } catch {
                toast.error("Failed to clear local drafts");
              }
              onOpenChange(false);
              setIsProcessing(false);
            }}
            className="h-10 sm:h-11 w-full sm:flex-1"
            data-slot="delete"
            disabled={isProcessing}
          >
            Delete
          </Button>
          <Button
            onClick={async () => {
              if (isProcessing || count <= 0) return;
              setIsProcessing(true);
              try {
                const raw = window.localStorage.getItem("unsavedReports");
                const entries: { id: string }[] = raw ? JSON.parse(raw) : [];
                const ids = entries.map((e) => e.id);
                if (ids.length > 0) {
                  const res = await fetch("/api/reports/attach", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ reportIds: ids }),
                  });
                  if (!res.ok) {
                    await res.text();
                    toast.error("Failed to save reports to history");
                    // Best-effort clear below still runs; return after toast to avoid success toast
                    try {
                      window.localStorage.removeItem("unsavedReports");
                    } catch {}
                    onOpenChange(false);
                    setIsProcessing(false);
                    return;
                  }
                }
                try {
                  window.localStorage.removeItem("unsavedReports");
                } catch {}
                toast.success("Reports saved to your history");
                router.refresh();
              } catch {
                toast.error("Something went wrong while saving");
              }
              onOpenChange(false);
              setIsProcessing(false);
            }}
            className="h-10 sm:h-11 w-full sm:flex-1"
            data-slot="save"
            disabled={isProcessing || count <= 0}
          >
            Save to History
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UnsavedReportsDialog;
